// How far from a planet's centre the camera has to stand to look it in the face. Kept out
// of three.js's scene graph, in the way of the other Utils modules, so the arithmetic can
// be tested under jsdom — the 3D chain itself cannot be imported there.

/** The share of the frame a landed body spans. The rest is air, so nothing touches the edge. */
export const LANDING_MARGIN = 0.95;

// Spare room between the planet's surface and the near plane, as a share of the near
// distance. Landing the surface exactly on the plane is the boundary case, and the wrong
// side of a boundary case is a hole cut through the planet, so a little room is kept.
const NEAR_CLEARANCE = 1.1;

/**
 * The distance from a planet's centre at which the camera frames it.
 *
 * The move lands the camera on one axis through the planet's centre, so the planet's own
 * radius and the frame's half-angle are all the framing needs: `radius / sin(halfAngle)` is
 * the distance at which a sphere of that radius spans the frame exactly, and the wanted
 * share of that angle is what leaves the margin.
 *
 * The angle is the narrower of the two the frame offers. three.js's fov is the vertical one,
 * so on a landscape viewport the vertical half-angle is the one to frame against — but a
 * portrait viewport is narrower across than it is tall, and a body framed on the vertical
 * angle alone overflows sideways on a phone.
 *
 * It cannot go closer than the camera's near plane allows. The renderer draws nothing
 * nearer than `near`, and a sphere inside it is dropped whole — the camera ends up aimed
 * at empty space, which is indistinguishable from having landed facing the wrong way. So
 * the surface is held clear of the plane at `radius + near`. At the camera's own near of
 * 0.1 this floor is slack for every body in the scene — it bites only below a radius of
 * about 0.044 — and it is kept so that a smaller body has a guard rather than a surprise.
 *
 * @param {number} radius - the planet's radius, in world units.
 * @param {number} fovDegrees - the camera's vertical field of view, in degrees.
 * @param {number} near - the camera's near plane.
 * @param {number} [aspect] - the camera's aspect ratio; 1 when there is no viewport to fit.
 * @returns {number} the distance from the planet's centre to stand the camera at.
 */
export function landingDistance(radius, fovDegrees, near, aspect = 1) {
    const halfVertical = (fovDegrees * Math.PI) / 360;
    const halfHorizontal = Math.atan(Math.tan(halfVertical) * aspect);
    const halfAngle = Math.min(halfVertical, halfHorizontal);

    const framing = radius / Math.sin(halfAngle * LANDING_MARGIN);

    return Math.max(framing, radius + near * NEAR_CLEARANCE);
}
